import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { DataTable } from "@/modules/shell/data-table";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildGrPrint } from "@/modules/docs/print/doc-print-math";
import { getGoodsReceipt, voidGoodsReceipt } from "@/shared/api/purchasing";
import { goodsReceiptLineColumns } from "./po-detail-columns";

export function GoodsReceiptDetailPage() {
  const { companySlug, receiptId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getGoodsReceipt>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    if (!companySlug || !receiptId) return;
    const d = await getGoodsReceipt(companySlug, receiptId);
    setData(d);
  }

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !receiptId) return;
    getGoodsReceipt(companySlug, receiptId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, receiptId, navigate]);

  async function onVoid() {
    if (!companySlug || !receiptId) return;
    const ok = await confirmUi({
      title: "Void this goods receipt?",
      description:
        "Stock and AP will reverse. The number is kept (gap OK).",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    /** One key for soft-confirm retry — plan/24 */
    const idempotencyKey = `gr-void-${crypto.randomUUID()}`;
    try {
      await voidGoodsReceipt(companySlug, receiptId, { idempotencyKey });
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug || !receiptId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading goods receipt…</Alert>
        )}
      </PageFrame>
    );
  }

  const { receipt: gr, lines } = data;
  const printData = buildGrPrint({
    number: gr.number,
    poNumber: gr.poNumber,
    lifecycle: gr.lifecycle,
    warehouseName: gr.warehouseName,
    warehouseCode: gr.warehouseCode,
    supplierInvoiceRef: gr.supplierInvoiceRef,
    issuedAt: gr.postedAt,
    lines: lines.map((l) => ({
      productName: l.productName,
      qty: l.qty,
      unitCost: l.unitCost,
    })),
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} />
      </div>
      <div className="no-print">
        <PageHeader
          eyebrow="Goods receipt"
          title={gr.number}
          description={[
            gr.lifecycle,
            `← ${gr.poNumber}`,
            gr.warehouseCode || gr.warehouseName,
            gr.supplierInvoiceRef
              ? `invoice ${gr.supplierInvoiceRef}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
              {gr.lifecycle === "posted" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onVoid()}
                >
                  Void receipt
                </Button>
              )}
            </>
          }
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="goods_receipt"
          docId={receiptId}
          linkPath={`/c/${companySlug}/purchasing/receipts/${receiptId}`}
        />
      </div>

      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[var(--color-muted)]">Warehouse</dt>
        <dd className="m-0">
          {gr.warehouseName}
          {gr.warehouseCode ? (
            <span className="font-mono text-xs text-[var(--color-muted)]">
              {" "}
              · {gr.warehouseCode}
            </span>
          ) : null}
        </dd>
        {gr.supplierInvoiceRef && (
          <>
            <dt className="text-[var(--color-muted)]">Supplier invoice</dt>
            <dd className="m-0">{gr.supplierInvoiceRef}</dd>
          </>
        )}
        {gr.postedAt && (
          <>
            <dt className="text-[var(--color-muted)]">Posted</dt>
            <dd className="m-0 text-xs">{gr.postedAt}</dd>
          </>
        )}
      </dl>

      <p className="mt-3 text-sm">
        <Link
          to={`/c/${companySlug}/purchasing/orders/${gr.purchaseOrderId}`}
          className="no-underline"
        >
          Open purchase order {gr.poNumber} →
        </Link>
      </p>

      <DataTable
        listId="goods-receipt-lines"
        companySlug={companySlug}
        className="mt-6 border-t border-[var(--color-border)]/90 pt-4"
        columns={goodsReceiptLineColumns()}
        data={lines}
        getRowId={(r) => r.id}
        manualSorting={false}
        empty={
          <EmptyState
            title="No lines"
            description="This receipt has no product lines."
          />
        }
      />

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="goods_receipt"
        docId={receiptId}
        linkPath={`/c/${companySlug}/purchasing/receipts/${receiptId}`}
      />
    </PageFrame>
  );
}
